import { z } from 'zod';

// Aspect extraction and theme clustering schemas for the AI analysis layer.
// Aspects are sub-sentence spans extracted from feedback records; themes are
// the clusters those aspects roll up into after embedding + grouping passes.

// --- Enums ------------------------------------------------------------------

export const PolaritySchema = z.enum(['positive', 'neutral', 'negative']);
export type Polarity = z.infer<typeof PolaritySchema>;

// Derived overall-sentiment band for a submission or aggregate. Shares the
// positive/neutral/negative vocabulary with PolaritySchema (the insights
// polarity dots reuse the same three states), but is a distinct type so a
// future divergence between the per-aspect polarity and the submission-level
// sentiment band doesn't silently couple the two surfaces. Banded from a
// 1-5 score by the sentiment_band SQL function (migration 0106): >= 3.5
// positive, <= 2.5 negative, else neutral.
export const SentimentBandSchema = z.enum(['positive', 'neutral', 'negative']);
export type SentimentBand = z.infer<typeof SentimentBandSchema>;

// The shared { score, band } shape every sentiment read returns. score is
// the 1-5 mean (null when nothing scored yet); band is that mean banded
// (null in lockstep with score).
export const SubmissionSentimentSchema = z.object({
  score: z.number().nullable(),
  band: SentimentBandSchema.nullable(),
});
export type SubmissionSentiment = z.infer<typeof SubmissionSentimentSchema>;

export const ThemeStatusSchema = z.enum(['active', 'archived', 'merged_into', 'dismissed']);
export type ThemeStatus = z.infer<typeof ThemeStatusSchema>;

export const ThemeSeverityHintSchema = z.enum(['low', 'medium', 'high', 'critical']);
export type ThemeSeverityHint = z.infer<typeof ThemeSeverityHintSchema>;

export const ThemeContentAxisSchema = z.enum(['game', 'infrastructure', 'third_party']);
export type ThemeContentAxis = z.infer<typeof ThemeContentAxisSchema>;

export const AttachmentMethodSchema = z.enum(['incremental', 'cluster', 'manual']);
export type AttachmentMethod = z.infer<typeof AttachmentMethodSchema>;

// --- Row schemas (portal read shape) ----------------------------------------

// embedding column is deliberately omitted: it is a pgvector binary column
// that should never be sent to the portal.
export const AspectSchema = z.object({
  id: z.string().uuid(),
  feedbackRecordId: z.string().uuid(),
  textSpan: z.string().min(1),
  polarity: PolaritySchema,
  themeId: z.string().uuid().nullable(),
  confidence: z.number().min(0).max(1),
  extractedAt: z.string().datetime({ offset: true }),
  promptVersion: z.string(),
  createdAt: z.string().datetime({ offset: true }),
});
export type Aspect = z.infer<typeof AspectSchema>;

export const ThemeSchema = z.object({
  id: z.string().uuid(),
  gameId: z.string().uuid(),
  title: z.string().min(1),
  description: z.string().nullable(),
  dominantPolarity: PolaritySchema,
  severityHint: ThemeSeverityHintSchema.nullable(),
  contentAxis: ThemeContentAxisSchema,
  attachmentRadius: z.number().nonnegative(),
  status: ThemeStatusSchema,
  mergedIntoId: z.string().uuid().nullable(),
  pinned: z.boolean(),
  dismissedAt: z.string().datetime({ offset: true }).nullable(),
  firstSeenAt: z.string().datetime({ offset: true }),
  lastMemberAddedAt: z.string().datetime({ offset: true }).nullable(),
  promptVersion: z.string().nullable(),
  modelRunId: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type Theme = z.infer<typeof ThemeSchema>;

export const ThemeMembershipSchema = z.object({
  id: z.string().uuid(),
  themeId: z.string().uuid(),
  aspectId: z.string().uuid(),
  attachedAt: z.string().datetime({ offset: true }),
  detachedAt: z.string().datetime({ offset: true }).nullable(),
  attachmentMethod: AttachmentMethodSchema,
  createdAt: z.string().datetime({ offset: true }),
});
export type ThemeMembership = z.infer<typeof ThemeMembershipSchema>;

// --- Tool output schemas (AI extraction layer) -------------------------------

export const AspectExtractionItemSchema = z.object({
  textSpan: z.string().min(1),
  polarity: PolaritySchema,
  confidence: z.number().min(0).max(1),
});
export type AspectExtractionItem = z.infer<typeof AspectExtractionItemSchema>;

export const AspectExtractionToolInputSchema = z.object({
  aspects: z.array(AspectExtractionItemSchema).min(1).max(8),
});
export type AspectExtractionToolInput = z.infer<typeof AspectExtractionToolInputSchema>;
